"use client";

import React from 'react';
import {Pie} from 'react-chartjs-2';
import {Chart as ChartJS, ArcElement, Tooltip, Legend} from 'chart.js';
import {AlertTriangle} from 'lucide-react';
import {useTranslation} from "react-i18next";
import axiosInstance from '@/api/axios';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export function WasteImpactCard({bins = [], period = 'year', year}) {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const {t} = useTranslation();

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const params = {
                    period,
                    bins
                };

                if (period === 'year' && year) {
                    params.year = year;
                }

                const response = await axiosInstance.get('/waste_impact/', {params});
                setData(response.data);
            } catch (err) {
                setError(err.message || t('wasteImpact.error'));
                console.error("Error fetching waste impact data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (bins.length > 0) {
            fetchData();
        }
    }, [bins, period, year, t]);

    if (loading) {
        return (
            <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
                <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center">
                        <p>{t('wasteImpact.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
                <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center p-4">
                        <p className="text-red-500 mb-4 text-center">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
                <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div className="aspect-[4/3] md:aspect-[16/9] flex items-center justify-center">
                        <p className="text-gray-500">{t('wasteImpact.noData')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Transform API data
    const wasteData = Object.entries(data.waste_streams).map(([wasteType, stats]) => ({
        name: wasteType,
        value: stats.waste,
        percentage: stats.percentage,
        unit: 'kg'
    }));

    const ghgData = Object.entries(data.waste_streams).map(([wasteType, stats]) => ({
        name: wasteType,
        value: stats.co2,
        percentage: (stats.co2 / data.total_co2) * 100,
        unit: 'kg CO₂e'
    }));

    const highestGHGStream = ghgData.reduce((max, item) =>
            item.percentage > max.percentage ? item : max,
        {name: '', percentage: 0}
    );

    const wastePercentageForHighestGHG = wasteData.find(item =>
        item.name === highestGHGStream.name
    )?.percentage || 0;

    const impactRatio = (highestGHGStream.percentage / wastePercentageForHighestGHG).toFixed(1);
    const isHighImpact = impactRatio > 1.5;

    // Chart.js data configuration
    const wasteChartData = {
        labels: wasteData.map(item => {
            // Comprehensive normalization:
            // 1. Convert to lowercase
            // 2. Trim whitespace
            // 3. Handle potential null/undefined values
            const streamName = item.name || "";
            const normalizedName = streamName.toLowerCase().trim();

            // For debugging
            console.log('Original stream name:', streamName);
            console.log('Normalized stream name:', normalizedName);
            console.log('Translation key:', `wasteImpact.streams.${normalizedName}`);

            const streamKey = `wasteImpact.streams.${normalizedName}`;
            const translation = t(streamKey, item.name);

            // For debugging
            console.log('Translation result:', translation);

            return translation;
        }),
        datasets: [{
            data: wasteData.map(item => item.value),
            backgroundColor: COLORS,
            borderWidth: 0
        }]
    };

// Same robust implementation for GHG chart
    const ghgChartData = {
        labels: ghgData.map(item => {
            const streamName = item.name || "";
            const normalizedName = streamName.toLowerCase().trim();
            const streamKey = `wasteImpact.streams.${normalizedName}`;
            return t(streamKey, item.name);
        }),
        datasets: [{
            data: ghgData.map(item => item.value),
            backgroundColor: COLORS,
            borderWidth: 0
        }]
    };

    // Plugin to display percentages in the pie charts
    const pieChartPercentagePlugin = {
        id: 'pieChartPercentage',
        afterDraw: (chart) => {
            const ctx = chart.ctx;
            const data = chart.data.datasets[0].data;
            const total = data.reduce((a, b) => a + b, 0);

            // Get the active data (percentage values) based on which chart is being drawn
            const percentageData = chart.canvas.id.includes('waste')
                ? wasteData.map(item => item.percentage)
                : ghgData.map(item => item.percentage);

            chart.getDatasetMeta(0).data.forEach((element, index) => {
                // Get the percentage value
                const percent = percentageData[index].toFixed(1) + '%';

                // Get the position of the slice
                const {x, y} = element.tooltipPosition();

                // Set text styles
                ctx.font = 'bold 12px Arial';
                ctx.fillStyle = 'white';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Draw the percentage text
                ctx.fillText(percent, x, y);
            });
        }
    };

    // Chart options for waste volume pie chart
    const wasteChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const item = wasteData[context.dataIndex];
                        return [
                            `${item.name.toUpperCase()}`,
                            `${t('wasteImpact.tooltip.amount')}: ${item.value.toFixed(1)} ${item.unit}`,
                            `${t('wasteImpact.tooltip.percentage')}: ${item.percentage.toFixed(1)}%`
                        ];
                    }
                }
            },
            legend: {display: false},
            datalabels: {
                display: false
            },
        },
        cutout: 0,
        radius: '70%'
    };

    // Chart options for climate impact pie chart
    const ghgChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const item = ghgData[context.dataIndex];
                        return [
                            `${item.name.toUpperCase()}`,
                            `${t('wasteImpact.tooltip.amount')}: ${item.value.toFixed(1)} ${item.unit}`,
                            `${t('wasteImpact.tooltip.percentage')}: ${item.percentage.toFixed(1)}%`
                        ];
                    }
                }
            },
            legend: {display: false},
            datalabels: {
                display: false
            },
        },
        cutout: 0,
        radius: '70%'
    };

    return (
        <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
            <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                <div className="flex items-center justify-between">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${isHighImpact ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                        {isHighImpact ? <AlertTriangle className="w-3 h-3"/> : null}
                        <span className="font-medium">{impactRatio}x</span>
                    </div>
                </div>

                <div className="aspect-[4/3] md:aspect-[16/9]">
                    <div className="flex flex-col h-full w-full gap-0">
                        {/* Key Statistic Banner */}
                        <div className="px-1">
                            <div className="text-center">
                                <div className="flex justify-center items-center gap-1">
                                    <div
                                        className={`px-2 py-1 rounded-md ${isHighImpact ? 'bg-destructive/10' : 'bg-primary/10'}`}>
                                        <p className="text-[0.65rem] font-medium text-muted-foreground">{t('wasteImpact.labels.waste').toUpperCase()}</p>
                                        <p className={`text-sm font-bold ${isHighImpact ? 'text-destructive' : 'text-primary'}`}>
                                            {Math.round(wastePercentageForHighestGHG)}%
                                        </p>
                                    </div>
                                    <div className="text-sm font-medium text-muted-foreground">→</div>
                                    <div
                                        className={`px-2 py-1 rounded-md ${isHighImpact ? 'bg-destructive/10' : 'bg-primary/10'}`}>
                                        <p className="text-[0.65rem] font-medium text-muted-foreground">CO₂</p>
                                        <p className={`text-sm font-bold ${isHighImpact ? 'text-destructive' : 'text-primary'}`}>
                                            {Math.round(highestGHGStream.percentage)}%
                                        </p>
                                    </div>
                                </div>
                                <p className="text-xs font-medium text-foreground mt-0.5">
                                    {`${(() => {
                                        const streamKey = `wasteImpact.streams.${highestGHGStream.name}`;
                                        return t(streamKey, highestGHGStream.name).toUpperCase();
                                    })()} ${t('wasteImpact.hasImpact')} ${impactRatio}x ${t('wasteImpact.impact')}`}</p>
                            </div>
                        </div>

                        {/* Pie Charts Section with Responsive Legend */}
                        <div className="flex-1 grid grid-cols-2 gap-0 relative min-h-0">
                            {/* Waste Volume Chart */}
                            <div className="flex flex-col h-full pt-0.5 min-h-0">
                                <h3 className="text-[0.90rem] font-medium text-center text-muted-foreground">{t('wasteImpact.labels.volume').toUpperCase()}</h3>
                                <div className="flex-1 min-h-0 relative">
                                    <Pie
                                        key={windowWidth}
                                        data={wasteChartData}
                                        options={wasteChartOptions}
                                        plugins={[pieChartPercentagePlugin]}
                                        id="waste-chart"
                                    />
                                </div>
                            </div>

                            {/* Responsive Centered Legend */}
                            <div
                                className="absolute left-1/2 transform -translate-x-1/2 bottom-1/3 flex flex-col items-center z-10">
                                <div className="bg-white/80 dark:bg-gray-800/80 rounded-lg p-1 md:p-2 shadow-sm">
                                    <ul className="flex flex-col gap-0.5 md:gap-1">
                                        {wasteData.map((item, index) => {
                                            const normalizedStreamName = item.name.toLowerCase().replace(/\s+/g, '');
                                            const translationKey = `wasteImpact.streams.${normalizedStreamName}`;
                                            const translatedName = t(translationKey, {defaultValue: item.name});

                                            return (
                                                <li key={item.name} className="flex items-center gap-1 md:gap-2">
                        <span
                            className="w-2 h-2 md:w-3 md:h-3 rounded-full"
                            style={{backgroundColor: COLORS[index % COLORS.length]}}
                        />
                                                    <span
                                                        className="text-[0.6rem] md:text-xs font-medium text-gray-700 dark:text-gray-300">
                            {translatedName.toUpperCase()}
                        </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>

                            {/* Climate Impact Chart */}
                            <div className="flex flex-col h-full pt-0.5 min-h-0">
                                <h3 className="text-[0.90rem] font-medium text-center text-muted-foreground">{t('wasteImpact.labels.impact').toUpperCase()}</h3>
                                <div className="flex-1 min-h-0 relative">
                                    <Pie
                                        key={windowWidth}
                                        data={ghgChartData}
                                        options={ghgChartOptions}
                                        plugins={[pieChartPercentagePlugin]}
                                        id="ghg-chart"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Actionable Insight */}
                        <div className="text-center mt-0.5">
                            <p className="text-xs font-medium text-foreground">
                                <span
                                    className={isHighImpact ? 'text-destructive' : 'text-primary'}>{t('wasteImpact.focus')}:</span> {t('wasteImpact.reduce')} {highestGHGStream.name}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}